import Vue from 'vue';
import { defineStore } from 'pinia';

// It is convention to prefix store names here with "use"
export const useReconciliationStore = defineStore('reconciliation', {
  state: () => ({
    resolvedSchema: {},
    formData: {},
    pendingDeletes: [], // Not using a Set because Vue's reactivity fails
    pendingSourceUpdates: {},
    metadata: {},
  }),

  getters: {
    topLevelKeys: state => Object.keys(state.formData || {}),
    isPendingDelete: state => path => {
      return state.pendingDeletes.includes(path);
    },
    getFieldOptions: state => name => {
      return state.metadata?.field_options?.[name] || [];
    },
    getFieldSource: state => name => {
      return state.metadata?.field_sources?.[name] || {};
    },
  },

  actions: {
    init(newSchema, draftDossier) {
      this.resolvedSchema = this.resolveRefs(newSchema, newSchema);

      // Reset formData deeply
      Object.keys(this.formData).forEach(k => Vue.delete(this.formData, k));

      if (this.resolvedSchema?.properties) {
        Object.entries(this.resolvedSchema.properties).forEach(
            ([key, prop]) => {
              Vue.set(
                  this.formData,
                  key,
                  this.getInitialValue(prop, draftDossier?.[key]),
              );
            },
        );
      }

      this.metadata = draftDossier.metadata;
    },

    // State management
    updateFormDataSection(path, value) {
      const parts = this.splitPath(path);

      const lastKey = parts[parts.length - 1];
      let parent = this.formData;

      for (let i = 0; i < parts.length - 1; i++) {
        parent = parent[parts[i]];
      }

      parent[lastKey] = value === undefined ? null : value;
    },

    addItemAtPath(path) {
      const parts = this.splitPath(path);

      const lastKey = parts[parts.length - 1];
      let parent = this.formData;

      // Walk to the parent object
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];

        // If an intermediate level doesn't exist, create it as an object
        if (parent[part] === undefined) {
          Vue.set(parent, part, {});
        }
        parent = parent[part];
      }

      // If the target array does not exist, initialize it reactively
      if (!Array.isArray(parent[lastKey])) {
        Vue.set(parent, lastKey, []);
      }

      const schemaNode = this.getSchemaAtPath(parts);
      const itemSchema = schemaNode?.items || null;

      if (!itemSchema) {
        console.warn(`No 'items' schema found for ${path}`);
        return;
      }

      const newItem = this.getInitialValue(itemSchema, null);
      parent[lastKey].push(newItem);
    },

    toggleItemDeletion(path) {
      const index = this.pendingDeletes.indexOf(path);

      if (index !== -1) {
        this.pendingDeletes.splice(index, 1);
      } else {
        this.pendingDeletes.push(path);
      }
    },

    addPendingSourceUpdate(path, source) {
      this.pendingSourceUpdates[path] = source;
    },

    removePendingSourceUpdate(path) {
      delete this.pendingSourceUpdates[path];
    },

    applySourceUpdates(fieldSources) {
      const updatedFieldSources = { ...fieldSources };

      Object.entries(this.pendingSourceUpdates).forEach(([path, source]) => {
        const existing = updatedFieldSources[path];

        if (!existing) {
          // New field source
          updatedFieldSources[path] = {
            ...source,
            source_history: [],
          };
        } else {
          const history = Array.isArray(existing.source_history) ?
            [...existing.source_history] :
            [];

          // Save snapshot of existing metadata (excluding source_history)
          const { source_history, ...snapshot } = existing;
          history.unshift({ ...snapshot });

          updatedFieldSources[path] = {
            ...source,
            source_history: history,
          };
        }
      });

      return updatedFieldSources;
    },

    applyPendingDeletes() {
      for (const path of this.pendingDeletes) {
        const parts = this.splitPath(path);
        const index = parts.pop();
        const parent = this.getValueFromArrayPath(this.formData, parts);

        if (Array.isArray(parent) && typeof index === 'number') {
          parent.splice(index, 1);
        }
      }
      this.pendingDeletes.clear();
    },

    getSchemaAtPath(pathParts) {
      let schemaNode = this.resolvedSchema;
      for (const part of pathParts) {
        if (schemaNode.anyOf) {
          const nonNull = schemaNode.anyOf.find(opt => opt.type !== 'null');
          schemaNode = nonNull || schemaNode.anyOf[0];
        }

        if (typeof part === 'number') {
          schemaNode = schemaNode.items;
        } else if (schemaNode?.properties?.[part]) {
          schemaNode = schemaNode.properties[part];
        } else {
          console.warn(`Could not find schema for path part: ${part}`);
          return null;
        }
      }

      // Final unwrap if leaf still has anyOf
      if (schemaNode.anyOf) {
        const nonNull = schemaNode.anyOf.find(opt => opt.type !== 'null');
        schemaNode = nonNull || schemaNode.anyOf[0];
      }

      return schemaNode;
    },

    generateFormData() {
      // 1) Deep clone
      const cleanFormData = JSON.parse(JSON.stringify(this.formData));

      // 2) Remove all marked deletions
      this.pendingDeletes.forEach(path => {
        const parts = this.splitPath(path);
        const index = parts[parts.length - 1];

        let parent = cleanFormData;
        for (let i = 0; i < parts.length - 1; i++) {
          parent = parent[parts[i]];
        }

        if (Array.isArray(parent) && typeof index === 'number') {
          parent.splice(index, 1);
        } else {
          console.warn(`Could not remove at ${path}: target is not an array.`);
        }
      });

      // 3) Remove unnecessary field options
      const metadataCopy = JSON.parse(
          JSON.stringify(this.metadata || {}),
      );
      delete metadataCopy.field_options;
      cleanFormData.metadata = metadataCopy;

      // 4) Apply the source updates
      const updatedFieldSources = this.applySourceUpdates(cleanFormData.metadata.field_sources);
      cleanFormData.metadata.field_sources = updatedFieldSources;

      // 5) Send it to your API
      console.log('Submitting clean formData:', cleanFormData);
      return cleanFormData;
    },

    // Utilities
    splitPath(path) {
      return path.split('.').flatMap(segment => {
        const parts = [];
        const bracketRegex = /\[(\d+)\]/g;

        const base = segment.replace(bracketRegex, '');
        if (base) parts.push(base);

        let match;
        while ((match = bracketRegex.exec(segment)) !== null) {
          parts.push(parseInt(match[1], 10)); // convert to number
        }

        return parts;
      });
    },

    resolveRefs(schema, root) {
      if (Array.isArray(schema)) {
        return schema.map(s => this.resolveRefs(s, root));
      }

      if (typeof schema === 'object' && schema !== null) {
        if (schema.$ref) {
          const path = schema.$ref.replace(/^#\//, '').split('/');
          let target = root;

          for (const segment of path) {
            if (target && segment in target) {
              target = target[segment];
            } else {
              console.warn(`Could not resolve $ref: ${schema.$ref}`);
              return {};
            }
          }

          return this.resolveRefs(target, root);
        } else {
          const resolved = {};
          for (const [k, v] of Object.entries(schema)) {
            resolved[k] = this.resolveRefs(v, root);
          }
          return resolved;
        }
      }

      return schema;
    },

    getInitialValue(prop, draftValue) {
      if (prop.anyOf) {
        const nonNull = prop.anyOf.find(opt => opt.type !== 'null');
        prop = nonNull || prop.anyOf[0];
      }

      if (prop.type === 'object' && prop.properties) {
        const obj = {};
        Object.entries(prop.properties).forEach(([k, v]) => {
          obj[k] = this.getInitialValue(v, draftValue?.[k]);
        });
        return obj;
      }

      if (prop.type === 'array') {
        return Array.isArray(draftValue) ?
          draftValue.map(item => this.deepClone(item)) :
          [];
      }

      if (prop.enum) {
        return draftValue ?? prop.enum[0] ?? '';
      }

      switch (prop.type) {
        case 'string':
          return draftValue ?? '';
        case 'number':
        case 'integer':
          return draftValue ?? 0;
        case 'boolean':
          return draftValue ?? false;
        case 'object':
          return this.deepClone(draftValue);
        default:
          return draftValue ?? null;
      }
    },

    deepClone(value) {
      if (Array.isArray(value)) {
        return value.map(item => this.deepClone(item));
      } else if (value && typeof value === 'object') {
        const cloned = {};
        Object.entries(value).forEach(([k, v]) => {
          cloned[k] = this.deepClone(v);
        });
        return cloned;
      }
      return value;
    },
  },
});
